import { useId } from 'react'
import { Form } from 'radix-ui'
import { ClaimFormSchema } from './types'
import { hasField, isFieldRequired, getFieldPattern, getFieldLabel } from './schema-utils'

type SchemaTextFieldProps = {
  schema: ClaimFormSchema
  section: string
  field: string
  label?: string
  defaultValue?: string
  placeholder?: string
  type?: string
  inputMode?: 'text' | 'numeric' | 'tel' | 'email'
  maxLength?: number
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  pattern?: string
  validationMessage?: string
  className?: string
}

export const SchemaTextField = ({
  schema,
  section,
  field,
  label,
  defaultValue,
  placeholder,
  type = 'text',
  inputMode,
  maxLength,
  value,
  onChange,
  pattern,
  validationMessage,
  className,
}: SchemaTextFieldProps) => {
  if (!hasField(schema, section, field)) {
    return null
  }

  const resolvedLabel = label ?? getFieldLabel(schema, section, field) ?? field
  const required = isFieldRequired(schema, section, field)
  const schemaPattern = pattern ?? getFieldPattern(schema, section, field)
  const fieldClassName = className ?? 'schema-field'

  return (
    <Form.Field name={field} className={`${fieldClassName}__field`}>
      <Form.Label className={`${fieldClassName}__label`}>
        {resolvedLabel} {required && '*'}
      </Form.Label>
      <Form.Message className={`${fieldClassName}__message`} match="valueMissing">
        {validationMessage ?? `Please enter ${resolvedLabel.toLowerCase()}`}
      </Form.Message>
      {schemaPattern && (
        <Form.Message className={`${fieldClassName}__message`} match="patternMismatch">
          {validationMessage ?? `Please enter a valid ${resolvedLabel.toLowerCase()}`}
        </Form.Message>
      )}
      {type === 'email' && (
        <Form.Message className={`${fieldClassName}__message`} match="typeMismatch">
          Please enter a valid email address
        </Form.Message>
      )}
      <Form.Control
        type={type}
        name={field}
        inputMode={inputMode}
        required={required}
        pattern={schemaPattern}
        maxLength={maxLength}
        defaultValue={value === undefined ? (defaultValue ?? '') : undefined}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${fieldClassName}__input-control`}
      />
    </Form.Field>
  )
}

type SchemaBooleanFieldProps = {
  schema: ClaimFormSchema
  section: string
  field: string
  label?: string
  value: boolean | undefined
  onChange: (value: boolean) => void
  className?: string
}

export const SchemaBooleanField = ({
  schema,
  section,
  field,
  label,
  value,
  onChange,
  className,
}: SchemaBooleanFieldProps) => {
  const labelId = useId()

  if (!hasField(schema, section, field)) {
    return null
  }

  const resolvedLabel = label ?? getFieldLabel(schema, section, field) ?? field
  const required = isFieldRequired(schema, section, field)
  const fieldClassName = className ?? 'schema-field'

  return (
    <section className={`${fieldClassName}__field`}>
      <span id={labelId} className={`${fieldClassName}__label`}>
        {resolvedLabel} {required && '*'}
      </span>
      <section
        role="radiogroup"
        aria-labelledby={labelId}
        className={`${fieldClassName}__radio-group`}>
        <label>
          <input type="radio" checked={value === true} onChange={() => onChange(true)} />
          Yes
        </label>
        <label>
          <input type="radio" checked={value === false} onChange={() => onChange(false)} />
          No
        </label>
      </section>
    </section>
  )
}
